import { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { DnDArea, File as FileItem } from "components/design/UploadFileSection";
import { FileIcon } from "components/design/FileIcon";
import { Cross } from "components/icons/Cross";
import { useToast } from "hooks/useToast";
import { useUploadIncomeStatementMutation } from "store/apis/estimatedTax";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { getTaxYearOptions } from "util/getTaxYearOptions";

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  year: string;
  showYearSelector?: boolean;
  initialSelectedYear?: string;
  onUploadSuccess?: () => void;
};

type ModalState = "upload" | "confirm_overwrite";

export const UploadIncomeStatementModal = ({
  open,
  onClose,
  entityId,
  year,
  showYearSelector = false,
  initialSelectedYear = "",
  onUploadSuccess,
}: Props) => {
  const { alertToast, successToast } = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const [modalState, setModalState] = useState<ModalState>("upload");
  const [overwriteWarning, setOverwriteWarning] = useState("");
  const [selectedYear, setSelectedYear] = useState(initialSelectedYear);

  const baseYearOptions = getTaxYearOptions(Number(year));
  const isInitialYearMissing =
    Boolean(initialSelectedYear) &&
    !baseYearOptions.some((option) => option.value === initialSelectedYear);
  const yearOptions = isInitialYearMissing
    ? [
        { value: initialSelectedYear, label: initialSelectedYear },
        ...baseYearOptions,
      ]
    : baseYearOptions;
  const effectiveYear = showYearSelector ? selectedYear : year;

  const [uploadIncomeStatement, { isLoading: isUploading }] =
    useUploadIncomeStatementMutation();

  const handleDrop = (dropped: File[]) => {
    setFiles(dropped.slice(0, 1));
  };

  const handleRemoveFile = () => {
    setFiles([]);
  };

  const buildFormData = (confirmOverwrite?: boolean) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("tax_year", effectiveYear);
    if (confirmOverwrite) formData.append("confirm_overwrite", "true");
    return formData;
  };

  const handleUpload = async (confirmOverwrite = false) => {
    if (files.length === 0 || !effectiveYear) return;
    try {
      await uploadIncomeStatement({
        entityId,
        formData: buildFormData(confirmOverwrite),
      }).unwrap();
      successToast({ message: `Income statement synced for ${effectiveYear}` });
      handleClose();
      onUploadSuccess?.();
    } catch (e: unknown) {
      const err = e as {
        status?: number;
        data?: { warning?: string; error?: string };
      };

      if (err.status === 409 && err.data?.warning) {
        setOverwriteWarning(err.data.warning);
        setModalState("confirm_overwrite");
        return;
      }

      alertToast({
        message: err.data?.error || "Failed to extract data from the file",
      });
      setFiles([]);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setModalState("upload");
    setOverwriteWarning("");
    setSelectedYear("");
    onClose();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) handleClose();
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Upload Income Statement</Modal.Title>
          <Modal.Close onClick={handleClose} />
        </Modal.Header>

        {modalState === "upload" ? (
          <>
            <Modal.Body className="t-space-y-3">
              <p className="t-text-body-sm t-text-text-30">
                Upload an income statement to sync financials for the selected
                tax year. Supported formats: PDF, CSV, XLSX, XLS, PNG, JPG.
              </p>
              {showYearSelector && (
                <Combobox
                  label="Select financial year"
                  required
                  options={yearOptions}
                  value={
                    yearOptions.find((o) => o.value === selectedYear) || null
                  }
                  onChange={(option) => {
                    const selected = option as OptionData | null;
                    setSelectedYear(selected ? selected.value : "");
                  }}
                  isClearable={false}
                  placeholder="Select year"
                  menuPortalTarget={document.body}
                />
              )}
              <DnDArea
                onDrop={handleDrop}
                accept={{
                  "application/pdf": [],
                  "text/csv": [],
                  "image/png": [],
                  "image/jpeg": [],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [],
                  "application/vnd.ms-excel": [],
                }}
                subText="PDF, CSV, XLSX, XLS, PNG, JPG · Up to 100MB"
              />
              {files.length > 0 && (
                <div className="t-flex t-flex-col t-gap-2">
                  {files.map((file) =>
                    isUploading ? (
                      <FileItem
                        key={file.name}
                        file={file}
                        isUploading
                        onCancel={() => {}}
                      />
                    ) : (
                      <div
                        key={file.name}
                        className="t-border t-border-solid t-rounded t-border-neutral-20 t-bg-surface-lighter-grey t-p-3 t-flex t-items-center t-gap-3 t-w-full"
                      >
                        <FileIcon fileType={file.type} size="40" />
                        <div className="t-flex t-flex-col t-gap-0.5 t-min-w-0">
                          <span className="t-text-blue-100 t-text-subtitle-sm t-truncate">
                            {file.name}
                          </span>
                          <span className="t-text-neutral t-text-caption">
                            {(file.size / 1024).toFixed(2)} KB
                          </span>
                        </div>
                        <Button
                          customType="ghost_icon"
                          size="small"
                          onClick={handleRemoveFile}
                        >
                          <Cross />
                        </Button>
                      </div>
                    )
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button disabled={isUploading} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                customType="primary"
                isLoading={isUploading}
                disabled={files.length === 0 || !effectiveYear || isUploading}
                onClick={() => handleUpload(false)}
              >
                Upload &amp; Sync
              </Button>
            </Modal.FooterButtonGroup>
          </>
        ) : (
          <>
            <Modal.Body>
              <div className="t-rounded-lg t-bg-orange-0 t-border t-border-orange-20 t-p-4 t-text-body-sm t-text-text-60">
                <p className="t-m-0">
                  {overwriteWarning ||
                    `Uploading a new statement for ${effectiveYear} will overwrite previously uploaded financials for that year. Do you want to continue?`}
                </p>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button disabled={isUploading} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                customType="danger"
                isLoading={isUploading}
                disabled={isUploading}
                onClick={() => handleUpload(true)}
              >
                Yes, Overwrite
              </Button>
            </Modal.FooterButtonGroup>
          </>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
